import { DATE_FORMAT } from '@constants';
import { Button, DateRangePicker } from '@gurupay/gp-ui-library';
import clsx from 'clsx';
import {
	addDays,
	addMonths,
	addWeeks,
	endOfDay,
	endOfMonth,
	endOfWeek,
	format,
	startOfDay,
	startOfMonth,
	startOfWeek,
} from 'date-fns';
import { useMemo } from 'react';
import type { DateRange, Matcher } from 'react-day-picker';
import type { Control, FieldError } from 'react-hook-form';
import { useController, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface DateRangeFormPickerWrapperProps {
	control: Control;
	required?: boolean;
	error?: FieldError;
	disabledDays?: Matcher;
	disabledFuture?: boolean;
	disabledPast?: boolean;
}

const TODAY = new Date();

const getDateFilters = (disabledFuture?: boolean) => {
	return [
		{
			value: { from: startOfDay(addDays(TODAY, -1)), to: endOfDay(addDays(TODAY, -1)) },
			labelKey: 'yesterday',
		},
		{
			value: {
				from: startOfMonth(addMonths(TODAY, -1)),
				to: endOfMonth(addMonths(TODAY, -1)),
			},
			labelKey: 'lastMonth',
		},
		{
			value: {
				from: startOfMonth(TODAY),
				to: disabledFuture ? endOfDay(TODAY) : endOfMonth(TODAY),
			},
			labelKey: 'thisMonth',
		},
		{
			value: {
				from: startOfWeek(addWeeks(TODAY, -1)),
				to: endOfWeek(addWeeks(TODAY, -1)),
			},
			labelKey: 'lastWeek',
		},
	];
};

export const DateRangeFormPickerWrapper = ({
	error,
	disabledFuture,
	disabledPast,
	control,
	required,
}: DateRangeFormPickerWrapperProps) => {
	const { t } = useTranslation('', { keyPrefix: 'filters' });
	const rules = required ? { required: t('required') } : undefined;
	const DATE_FILTERS = getDateFilters(disabledFuture);
	const { field: dateFromField } = useController({ name: 'dateFrom', control, rules });
	const { field: dateToField } = useController({ name: 'dateTo', control, rules });

	const [dateFrom, dateTo] = useWatch({ control, name: ['dateFrom', 'dateTo'] });

	const fieldValue =
		dateFrom && dateTo ? { from: new Date(dateFrom), to: new Date(dateTo) } : undefined;

	const activeBtn = useMemo(() => {
		if (!dateFrom || !dateTo) {
			return null;
		}

		return DATE_FILTERS.findIndex((item) => {
			const from = format(item.value.from!, DATE_FORMAT);
			const to = format(item.value.to!, DATE_FORMAT);
			return from === dateFrom && to === dateTo;
		});
	}, [dateFrom, dateTo]);

	const fieldOnChange = (date?: DateRange) => {
		dateFromField.onChange(date?.from ? format(date.from, DATE_FORMAT) : undefined);
		dateToField.onChange(date?.to ? format(date.to, DATE_FORMAT) : undefined);
	};

	return (
		<div className="flex flex-col gap-space-8">
			<DateRangePicker
				allowClear
				disabledFuture={disabledFuture}
				disabledPast={disabledPast}
				endMonth={new Date()}
				error={error?.message}
				label={t('dateRange')}
				onChange={fieldOnChange}
				placeholder="YYYY-MM-DD - YYYY-MM-DD"
				startMonth={new Date(2015, 1)}
				value={fieldValue}
			/>

			<div className="flex gap-space-8 overflow-x-auto md:overflow-visible no-scrollbar">
				{DATE_FILTERS.map((item, idx) => (
					<Button
						key={item.labelKey}
						aria-pressed={activeBtn === idx}
						className={clsx(
							'whitespace-nowrap',
							activeBtn === idx && 'border border-neutral-50',
						)}
						onClick={() => fieldOnChange(item.value)}
						size="sm"
						type="button"
						variant={activeBtn === idx ? 'secondary' : 'outlined'}
					>
						{t(item.labelKey)}
					</Button>
				))}
			</div>
		</div>
	);
};
